import React, { useState, useEffect } from 'react';
import { DealColorPicker, DealTag } from '../components/DealColorPicker';
import { useAuth } from '../context/AuthContext';
import { loadTransactionTasks as fetchTxTasks, completeTransactionTask, cleanDealTasks, startPostClosingFollowUp, activateLiveListingTasks, setListingLiveDate, backdateSafeTask, releaseClosedTasks } from '../services/transactionTasks';
import { getStats, awardForTask } from '../services/gamification';
import { update, ref } from 'firebase/database';
import { database } from '../firebaseConfig';
import { isAutomatic, isMyTask, isClientEmailTask, isDealAgent, hiddenFromLimited } from '../services/taskRules';
import { canEditTask, canUseQuickActions, showsPoints } from '../config/roleAccess';
import { canUseMarketingTools } from '../config/marketingTools';
import { needsBrochureLink, isBrochureReview, isCanvaLink, runBrochureStep } from '../services/brochureFlow';
import TaskKindLabel from './TaskKindLabel';
import AddClientTask from './AddClientTask';
import { taskKind } from '../utils/taskKind';
import InspectorQuickContact from './InspectorQuickContact';
import StagingQuickContact from './StagingQuickContact';
import PhotographerQuickText from './PhotographerQuickText';
import { celebrateTaskDone } from '../utils/celebrateTask';
import PhotographyClientText from './PhotographyClientText';
import MatterportClientText, { MatterportScheduleAction } from './MatterportClientText';
import FeatureCardsFlow from './FeatureCardsFlow';
import MaterialApprovalFlow from './MaterialApprovalFlow';
import FeatureUpgradeFlow from './FeatureUpgradeFlow';
import SocialGraphicsFlow from './SocialGraphicsFlow';
import HomeSaleContactQuickContact from './HomeSaleContactQuickContact';
import ClientQuickContact from './ClientQuickContact';
import TitleInitiationFlow from './TitleInitiationFlow';
import LenderQuickContact, { lenderFromDeal } from './LenderQuickContact';
import OtherAgentQuickText from './OtherAgentQuickText';
import HomeSaleAgentQuickContact, { homeSaleAgentOf } from './HomeSaleAgentQuickContact';
import SellerQuestionnaireQuickContact from './SellerQuestionnaireQuickContact';
import ClientBirthdayQuickEntry from './ClientBirthdayQuickEntry';
import GoogleReviewQuickText from './GoogleReviewQuickText';
import FollowUpTextQuickEntry from './FollowUpTextQuickEntry';
import { updateTransaction } from '../services/transactionService';
import { compareByDueDate } from '../utils/sortTasks';
import { Clock, AlertCircle, CheckCircle2, Trophy, Flame } from 'lucide-react';
import { roleLabel } from '../config/roles';
import { isOverdueDue, OVERDUE_RED } from '../utils/dueDates';

const GOLD = '#B8962E';
const INK = '#1A1A1A';
const MUTED = '#5B6270';
const LINE = '#E1E4EA';

export default function TransactionTasksList({ transactionId, ownerUid, listingAgent, client, dealAddress, deal, otherAgent, onlyTaskIds, compact, initialView }) {
  const { user, userRole } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const pointsOn = showsPoints(userRole);
  const quickOn = canUseQuickActions(userRole);
  const [statFilter, setStatFilter] = useState('todo'); // one-tap stat boxes: all | done | todo | overdue
  const [view, setView] = useState(initialView || 'mine');
  const [stats, setStats] = useState({ points: 0, streak: 0 });
  const [celebration, setCelebration] = useState(null);
  const [closingInputs, setClosingInputs] = useState({});

  useEffect(() => {
    loadTasksNow();
  }, [user?.uid, transactionId]);

  useEffect(() => {
    if (!celebration) return undefined;
    const timer = setTimeout(() => setCelebration(null), 4000);
    return () => clearTimeout(timer);
  }, [celebration]);

  const loadTasksNow = async () => {
    try {
      setLoading(true);
      setError(null);
      if (user?.uid) {
        try { await cleanDealTasks(transactionId, deal); } catch (err) { console.error('Could not clean deal tasks', err); }
        const [txTasks, saved] = await Promise.all([
          fetchTxTasks([transactionId]),
          getStats(user.uid),
        ]);
        txTasks.sort(compareByDueDate);
        setTasks(onlyTaskIds ? txTasks.filter(t => onlyTaskIds.includes(t.id)) : txTasks);
        setStats({ points: saved.points, streak: saved.streak });
      }
    } catch (err) {
      console.error('Error loading transaction tasks:', err);
      setError('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (taskId, newStatus, node = null) => {
    if (!canEditTask(tasks.find(t => t.id === taskId), userRole, user?.uid)) return;
    try {
      const name = user?.displayName || user?.email || 'Unknown';
      if (newStatus === 'completed') {
        const task = tasks.find(t => t.id === taskId);
        if (isBrochureReview(task)) { setError('Use Approve or Request changes on the brochure review.'); return; }
        // Brochure tasks complete only with a Canva link; completing sends it to the listing agent for review.
        if (needsBrochureLink(task)) {
          const link = String(brochureLinks[taskId] ?? task.brochureLink ?? '').trim();
          if (!isCanvaLink(link)) { setError('Paste the finished Canva brochure link (https://www.canva.com/...) before completing this task.'); return; }
          await update(ref(database, `transactions/${transactionId}/tasks/${taskId}`), { brochureLink: link });
          await completeTransactionTask(transactionId, taskId, name);
          await runBrochureStep(transactionId, ownerUid, 'submit', { task, address: dealAddress, link, actorName: name });
        } else
        await completeTransactionTask(transactionId, taskId, name);
        if (task && user?.uid) {
          const result = pointsOn ? await awardForTask(user.uid, task) : { points: 0, streak: 0, earned: 0 };
          setStats({ points: result.points, streak: result.streak });
          setCelebration({ earned: result.earned, onTime: result.onTime, streak: result.streak });
        }
      } else {
        await update(ref(database, `transactions/${transactionId}/tasks/${taskId}`), { status: newStatus, completedBy: null, completedAt: null });
      }
      if (newStatus === 'completed' && node) celebrateTaskDone(node, loadTasksNow);
      else loadTasksNow();
    } catch (err) {
      console.error('Could not update task', err);
      setError('Could not update that task.');
    }
  };

  // Brochure link field (MM) and Approve / Request changes (agent).
  const [brochureLinks, setBrochureLinks] = useState({});
  const [brochureComments, setBrochureComments] = useState({});
  const handleBrochureReview = async (task, action) => {
    if (!canEditTask(task, userRole, user?.uid)) return;
    try {
      const name = user?.displayName || user?.email || 'Unknown';
      if (action === 'changes' && !String(brochureComments[task.id] || '').trim()) { setError('Add a comment saying what to change.'); return; }
      await runBrochureStep(transactionId, ownerUid, action, { task, address: dealAddress, link: task.brochureLink, comment: brochureComments[task.id] || '', actorName: name });
      await update(ref(database, `transactions/${transactionId}/tasks/${task.id}`), { brochureDecision: action === 'approve' ? 'approved' : 'changes requested', agentComments: brochureComments[task.id] || null });
      await completeTransactionTask(transactionId, task.id, name);
      loadTasksNow();
    } catch (err) { console.error('Brochure review failed', err); setError(err.message || 'Could not save that.'); }
  };

  // Yes on "Has this listing gone live on the MLS?" sets the listing to Live, starts the Live Listing tasks, and completes the check.
  const [liveDates, setLiveDates] = useState({});
  const [liveSaving, setLiveSaving] = useState(null);
  const localToday = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; };
  const handleLiveCheck = async (task) => {
    if (!canEditTask(task, userRole, user?.uid)) return;
    const liveDate = liveDates[task.id] || localToday();
    if (liveDate > localToday()) { setError('The live date cannot be in the future.'); return; }
    setLiveSaving(task.id);
    try {
      const name = user?.displayName || user?.email || 'Unknown';
      // Records "Date Listed Live on MLS", sets Listing Status to Live and starts the live tasks. Backdated dates create no past-due tasks or emails.
      await setListingLiveDate(transactionId, ownerUid || user?.uid, liveDate, name);
      await completeTransactionTask(transactionId, task.id, name).catch(() => {});
      if (user?.uid) {
        const result = pointsOn ? await awardForTask(user.uid, task) : { points: 0, streak: 0, earned: 0 };
        setStats({ points: result.points, streak: result.streak });
        setCelebration({ earned: result.earned, onTime: result.onTime, streak: result.streak });
      }
      loadTasksNow();
    } catch (err) {
      console.error('Could not set listing live', err);
      setError('Could not set the listing to live.');
    } finally {
      setLiveSaving(null);
    }
  };

  // Marks the seller listing as live and completes the Mark Listing as Live task.
  const isMarkLive = (task) => /^mark listing as live$/i.test(task.name || '');
  const handleMarkLive = async (task) => {
    if (!canEditTask(task, userRole, user?.uid)) return;
    try {
      const name = user?.displayName || user?.email || 'Unknown';
      await updateTransaction(ownerUid || user?.uid, transactionId, {
        status: 'live',
        listingStatus: 'live',
        liveAt: new Date().toISOString(),
      });
      await activateLiveListingTasks(transactionId);
      await completeTransactionTask(transactionId, task.id, name);
      if (user?.uid) {
        const result = pointsOn ? await awardForTask(user.uid, task) : { points: 0, streak: 0, earned: 0 };
        setStats({ points: result.points, streak: result.streak });
        setCelebration({ earned: result.earned, onTime: result.onTime, streak: result.streak });
      }
      loadTasksNow();
    } catch (err) {
      console.error('Could not mark listing as live', err);
      setError('Could not mark the listing as live.');
    }
  };

  // Saves the actual closing date, marks the deal closed, and completes the task.
  const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const handleSaveClosing = async (task) => {
    if (!canEditTask(task, userRole, user?.uid)) return;
    const date = closingInputs[task.id] || todayIso();
    try {
      const name = user?.displayName || user?.email || 'Unknown';
      await updateTransaction(ownerUid || user?.uid, transactionId, {
        actualClosingDate: date,
        status: 'closed',
        closedAt: new Date().toISOString(),
      });
      await completeTransactionTask(transactionId, task.id, name);
      await startPostClosingFollowUp(transactionId, date, user?.uid);
      await releaseClosedTasks(transactionId, date, user?.uid, deal?.type, { justSoldPostcards: deal?.createJustSoldPostcards === true });
      if (user?.uid) {
        const result = pointsOn ? await awardForTask(user.uid, task) : { points: 0, streak: 0, earned: 0 };
        setStats({ points: result.points, streak: result.streak });
        setCelebration({ earned: result.earned, onTime: result.onTime, streak: result.streak });
      }
      loadTasksNow();
    } catch (err) {
      console.error('Could not save closing date', err);
      setError('Could not save the closing date.');
    }
  };

  // "Mark as Closed": shared by the TC's closed-settlement-date task and the agent's "Did this transaction close today?".
  // Whichever is clicked first closes the deal and completes BOTH tasks. Safe to click twice (no duplicates).
  const CLOSE_TASK = /did this transaction close today|closed settlement date: update the contract to close/i;
  const isCloseTask = (task) => CLOSE_TASK.test(task?.name || '') || task?.action === 'enter-closing-date';
  const [closeBusy, setCloseBusy] = useState(false);
  const [savedNote, setSavedNote] = useState('');
  const defaultCloseDate = () => {
    const f = deal?.underContract?.closedDate || deal?.underContract?.forecastedClosedDate || '';
    return f && f <= todayIso() ? f : todayIso();
  };
  const handleMarkClosed = async (task) => {
    if (!canEditTask(task, userRole, user?.uid) || closeBusy) return;
    const date = closingInputs[task.id] || defaultCloseDate();
    if (date > todayIso()) { setError('The closed date cannot be in the future.'); return; }
    const pretty = new Date(`${date}T12:00:00`).toLocaleDateString();
    if (!window.confirm(`Mark this transaction as Closed?\n\nClosed (Settlement) Date: ${pretty}\n\nThis sets the deal to Closed, saves the date, and completes the closing tasks for everyone. Any client emails that follow wait for approval.`)) return;
    setCloseBusy(true); setError(null);
    try {
      const name = user?.displayName || user?.email || 'Unknown';
      const owner = ownerUid || user?.uid;
      const alreadyClosed = deal?.status === 'closed';
      const patch = { actualClosingDate: date, closedAt: deal?.closedAt || new Date().toISOString(), 'underContract/closedDate': date };
      if (!alreadyClosed) patch.status = 'closed';
      await update(ref(database, `users/${owner}/transactions/${transactionId}`), { ...patch, updatedAt: new Date().toISOString() });
      // Complete every open closing task on this deal (TC's and agent's), so it disappears for the other person too.
      const open = tasks.filter((t) => isCloseTask(t) && t.status !== 'completed' && t.status !== 'na');
      for (const t of open) await completeTransactionTask(transactionId, t.id, name);
      await startPostClosingFollowUp(transactionId, date, user?.uid); // skips tasks that already exist
      await releaseClosedTasks(transactionId, date, user?.uid, deal?.type, { justSoldPostcards: deal?.createJustSoldPostcards === true });
      if (user?.uid) {
        const result = pointsOn ? await awardForTask(user.uid, task) : { points: 0, streak: 0, earned: 0 };
        setStats({ points: result.points, streak: result.streak });
      }
      setSavedNote(`Saved. Marked Closed on ${pretty}.`);
      loadTasksNow();
    } catch (err) {
      console.error('Could not mark closed', err);
      setError('Could not mark the transaction closed. Please try again.');
    } finally {
      setCloseBusy(false);
    }
  };

  // "Not closing today": pick the new anticipated closing date. Updates the deal's forecast and moves every open
  // closing-date task (both closing tasks and anything counted from the closing date) to match. Shared like Mark as Closed.
  // Tasks that would land in the past are due today; emails that would land in the past are skipped, never sent late.
  const [notClosingFor, setNotClosingFor] = useState(null);
  const [newCloseDate, setNewCloseDate] = useState('');
  const CLOSING_ANCHORS = ['closingDate', 'forecastedClosingDate', 'settlementDate'];
  const plusDays = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + (Number(n) || 0)); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const handleNotClosing = async (task) => {
    if (!canEditTask(task, userRole, user?.uid) || closeBusy) return;
    const date = newCloseDate;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || date <= todayIso()) { setError('Pick the new anticipated closing date (after today).'); return; }
    setCloseBusy(true); setError(null);
    try {
      const owner = ownerUid || user?.uid;
      await update(ref(database, `users/${owner}/transactions/${transactionId}`), { 'underContract/forecastedClosedDate': date, anticipatedClosingDate: date, updatedAt: new Date().toISOString() });
      const today = todayIso();
      const updates = {};
      tasks.forEach((t) => {
        if (t.status === 'completed' || t.status === 'na' || t.status === 'skipped') return;
        const byAnchor = CLOSING_ANCHORS.includes(t.anchor);
        if (!byAnchor && !isCloseTask(t)) return;
        const due = byAnchor ? plusDays(date, t.offsetDays) : date;
        const safe = backdateSafeTask({ ...t, dueDate: due }, today);
        updates[`transactions/${transactionId}/tasks/${t.id}/dueDate`] = safe.dueDate;
        if (safe.status === 'skipped') {
          updates[`transactions/${transactionId}/tasks/${t.id}/status`] = 'skipped';
          updates[`transactions/${transactionId}/tasks/${t.id}/mode`] = 'manual';
          updates[`transactions/${transactionId}/tasks/${t.id}/skippedReason`] = safe.skippedReason;
        }
      });
      if (Object.keys(updates).length) await update(ref(database), updates);
      setNotClosingFor(null); setNewCloseDate('');
      setSavedNote(`Saved. New anticipated closing date ${new Date(`${date}T12:00:00`).toLocaleDateString()}. Closing tasks moved to match.`);
      loadTasksNow();
    } catch (err) {
      console.error('Could not reschedule closing', err);
      setError('Could not save the new closing date. Please try again.');
    } finally {
      setCloseBusy(false);
    }
  };

  // Sending the message counts as done: completes the task and awards points.
  const handleSent = async (task) => {
    if (!canEditTask(task, userRole, user?.uid)) return;
    try {
      const name = user?.displayName || user?.email || 'Unknown';
      await completeTransactionTask(transactionId, task.id, name);
      if (user?.uid) {
        const result = pointsOn ? await awardForTask(user.uid, task) : { points: 0, streak: 0, earned: 0 };
        setStats({ points: result.points, streak: result.streak });
        setCelebration({ earned: result.earned, onTime: result.onTime, streak: result.streak });
      }
      loadTasksNow();
    } catch (err) {
      console.error('Could not mark task done after sending', err);
      setError('Could not mark that task done.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No date yet';
    const date = new Date(`${dateStr}T00:00:00`);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isOverdue = (dueDate, status) => isOverdueDue(dueDate, status);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return '#D64545';
      case 'high': return '#E08A00';
      case 'medium': return GOLD;
      case 'low': return '#3C9D5D';
      default: return '#9AA1AD';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 size={20} style={{ color: '#3C9D5D' }} />;
      case 'in-progress':
        return <Clock size={20} style={{ color: '#E08A00' }} />;
      default:
        return <AlertCircle size={20} style={{ color: '#9AA1AD' }} />;
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <h3 style={styles.sectionTitle}>Transaction Tasks</h3>
        <div style={styles.box}>
          <p style={styles.mutedText}>Loading tasks...</p>
        </div>
      </div>
    );
  }

  const viewerIsDealAgent = isDealAgent(deal, user?.uid, ownerUid);
  const viewTasks = (view === 'mine' ? tasks.filter(t => isMyTask(t, userRole, { dealAgent: viewerIsDealAgent })) : tasks).filter(t => !hiddenFromLimited(t, userRole));
  const isOpenTask = (t) => t.status !== 'completed' && t.status !== 'na';
  const filteredTasks = viewTasks.filter(t => {
    if (statFilter === 'done') return t.status === 'completed';
    if (statFilter === 'overdue') return isOverdue(t.dueDate, t.status);
    if (statFilter === 'all') return true;
    return showCompleted || isOpenTask(t);
  });
  const doneCount = viewTasks.filter(t => t.status === 'completed').length;
  const percent = viewTasks.length ? Math.round((doneCount / viewTasks.length) * 100) : 0;
  const overdueCount = viewTasks.filter(t => isOverdue(t.dueDate, t.status)).length;

  return (
    <div style={compact ? { ...styles.container, marginTop: 0, paddingTop: 0, borderTop: 'none' } : styles.container}>
      <div style={compact ? styles.hidden : styles.headerRow}>
        <h3 style={styles.sectionTitle}>Transaction Tasks</h3>
        <div style={pointsOn ? styles.scoreRow : styles.hidden}>
          <span style={styles.scorePill}><Trophy size={14} color={GOLD} /> {stats.points} points</span>
          <span style={styles.scorePill}><Flame size={14} color="#E08A00" /> {stats.streak}-day streak</span>
        </div>
      </div>

      {celebration && pointsOn && (
        <div style={styles.celebration} role="status">
          <strong style={styles.celebrationTitle}>Nice work! +{celebration.earned} points</strong>
          <span style={styles.celebrationText}>
            {celebration.onTime ? 'Finished on time. ' : ''}
            {celebration.streak > 1 ? `${celebration.streak} days in a row.` : 'Streak started. Keep it going tomorrow.'}
          </span>
        </div>
      )}

      {tasks.length > 0 && (
        <div style={compact ? styles.hidden : styles.progressWrap}>
          <div style={styles.progressLabelRow}>
            <span style={styles.progressLabel}>{doneCount} of {viewTasks.length} done</span>
            <span style={styles.progressLabel}>{percent}%</span>
          </div>
          <div style={styles.progressTrack} aria-hidden="true">
            <div style={{ ...styles.progressFill, width: `${percent}%` }} />
          </div>
        </div>
      )}

      <div style={compact ? styles.hidden : styles.toolRow}>
        {tasks.length > 0 && (
          <div style={styles.viewToggle}>
            <button type="button" onClick={() => setView('mine')} style={view === 'mine' ? styles.toggleBtnActive : styles.toggleBtn}>
              My tasks
            </button>
            <button type="button" onClick={() => setView('all')} style={view === 'all' ? styles.toggleBtnActive : styles.toggleBtn}>
              All tasks
            </button>
          </div>
        )}
        {tasks.length > 0 && (
          <label style={styles.filterLabel}>
            <input type="checkbox" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} style={styles.checkbox} />
            Show completed
          </label>
        )}
      </div>

      {savedNote && (
        <div role="status" style={{ background: '#E8F5E9', border: '1px solid #2e7d32', color: '#1b5e20', borderRadius: 8, padding: '10px 14px', margin: '8px 0', fontWeight: 600 }}>✓ {savedNote}</div>
      )}
      {error && (
        <div style={styles.errorBox}>
          <p style={styles.errorText}>{error}</p>
        </div>
      )}

      {tasks.length === 0 ? (
        <div style={styles.box}>
          <p style={styles.mutedText}>No tasks associated with this transaction yet</p>
        </div>
      ) : (
        <div style={styles.tasksList}>
          <div style={compact ? styles.hidden : styles.statsRow}>
            <div role="button" tabIndex={0} aria-pressed={statFilter === 'all'} onClick={() => setStatFilter('all')} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setStatFilter('all')}
              style={{ ...styles.statItem, cursor: 'pointer', ...(statFilter === 'all' ? styles.statItemActive : {}) }}>
              <span style={styles.statLabel}>Total</span>
              <span style={styles.statValue}>{viewTasks.length}</span>
            </div>
            <div role="button" tabIndex={0} aria-pressed={statFilter === 'done'} onClick={() => setStatFilter('done')} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setStatFilter('done')}
              style={{ ...styles.statItem, cursor: 'pointer', ...(statFilter === 'done' ? styles.statItemActive : {}) }}>
              <span style={styles.statLabel}>Completed</span>
              <span style={styles.statValue}>{doneCount}</span>
            </div>
            <div role="button" tabIndex={0} aria-pressed={statFilter === 'todo'} onClick={() => setStatFilter('todo')} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setStatFilter('todo')}
              style={{ ...styles.statItem, cursor: 'pointer', ...(statFilter === 'todo' ? styles.statItemActive : {}) }}>
              <span style={styles.statLabel}>To do</span>
              <span style={styles.statValue}>{viewTasks.filter(t => t.status !== 'completed').length}</span>
            </div>
            <div role="button" tabIndex={0} aria-pressed={statFilter === 'overdue'} onClick={() => setStatFilter('overdue')} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setStatFilter('overdue')}
              style={{ ...styles.statItem, cursor: 'pointer', ...(statFilter === 'overdue' ? styles.statItemActive : {}) }}>
              <span style={styles.statLabel}>Overdue</span>
              <span style={{ ...styles.statValue, color: overdueCount ? '#D64545' : '#3C9D5D' }}>{overdueCount}</span>
            </div>
          </div>

          {!compact && transactionId && user?.uid && (isDealAgent(deal, user.uid, ownerUid) || userRole === 'Admin') && (
            <div style={{ margin: '0 0 12px' }}>
              <AddClientTask uid={user.uid} transactionId={transactionId} contactId={deal?.crmContactId || deal?.contactId || ''}
                contactName={client || ''} hint="Added to this deal as a Manual Agent task, so you and any co-agent on the deal see it."
                onAdded={() => loadTasksNow()} />
            </div>
          )}
          <div style={styles.tasksTable}>
            {filteredTasks.length === 0 ? (
              <div style={styles.box}>
                <p style={styles.mutedText}>{showCompleted ? 'No tasks' : 'All caught up. Nice work!'}</p>
              </div>
            ) : (
              filteredTasks.map((task) => {
                const overdue = isOverdue(task.dueDate, task.status);
                const done = task.status === 'completed';
                return (
                  <React.Fragment key={task.id}>
                  <div
                    data-task-card="true"
                    style={{
                      ...styles.taskRow,
                      backgroundColor: overdue ? '#FDE3E3' : '#FFFFFF',
                      borderLeftColor: overdue ? OVERDUE_RED : (deal?.dealColor || '#B8962E'),
                      opacity: done ? 0.65 : 1,
                    }}
                  >
                    <div style={styles.taskMainSection}>
                      <div style={styles.taskStatusIcon}>{getStatusIcon(task.status)}</div>
                      <div style={styles.taskContent}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                          <DealTag color={deal?.dealColor} address={dealAddress} />
                          <TaskKindLabel kind={taskKind({ transactionId, ...task })} />
                        </div>
                        <h4 style={{ ...styles.taskTitle, textDecoration: done ? 'line-through' : 'none' }}>
                          {task.title}
                          {isAutomatic(task) && <span style={styles.badge}>Automatic</span>}
                          {task.completedBy && <span style={styles.badge}>Done by {task.completedBy}</span>}
                        </h4>
                        {task.description && <p style={styles.taskDescription}>{task.description}</p>}
                      </div>
                    </div>

                    <div style={styles.taskDetailsSection}>
                      <div style={styles.detailItem}>
                        <span style={styles.detailLabel}>Due</span>
                        <span style={{ ...styles.detailValue, color: overdue ? '#B42323' : INK, fontWeight: overdue ? 700 : undefined }}>{formatDate(task.dueDate)}{overdue ? ' (OVERDUE)' : ''}</span>
                      </div>
                      <div style={styles.detailItem}>
                        <span style={styles.detailLabel}>Role</span>
                        <span style={styles.detailValue}>{roleLabel(task.role)}</span>
                      </div>
                      {!done && needsBrochureLink(task) && (
                        <div style={{ ...styles.detailItem, flexBasis: '100%' }}>
                          <span style={styles.detailLabel}>Brochure link</span>
                          <input type="url" placeholder="https://www.canva.com/design/..." value={brochureLinks[task.id] ?? task.brochureLink ?? ''} onChange={(e) => setBrochureLinks((m) => ({ ...m, [task.id]: e.target.value }))} style={{ width: '100%', minHeight: 40, padding: '6px 10px', boxSizing: 'border-box' }} />
                        </div>
                      )}
                      {task.brochureLink && !needsBrochureLink(task) && (
                        <div style={styles.detailItem}><a href={task.brochureLink} target="_blank" rel="noreferrer" style={{ color: '#8C6D1F', fontWeight: 600 }}>Open brochure in Canva</a></div>
                      )}
                      {task.agentComments && <div style={{ ...styles.detailItem, flexBasis: '100%' }}><span style={styles.detailLabel}>Agent comments</span><span style={styles.detailValue}>{task.agentComments}</span></div>}
                      {!done && isBrochureReview(task) && (
                        <div style={{ ...styles.detailItem, flexBasis: '100%', gap: 8 }}>
                          <textarea placeholder="Comments (needed for Request changes)" value={brochureComments[task.id] || ''} onChange={(e) => setBrochureComments((m) => ({ ...m, [task.id]: e.target.value }))} style={{ width: '100%', minHeight: 60, boxSizing: 'border-box' }} />
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button type="button" onClick={() => handleBrochureReview(task, 'approve')} style={{ minHeight: 44, padding: '0 14px', background: '#111', color: '#D4AF37', border: '1px solid #D4AF37', borderRadius: 8, fontWeight: 600 }}>Approve</button>
                            <button type="button" onClick={() => handleBrochureReview(task, 'changes')} style={{ minHeight: 44, padding: '0 14px', background: '#fff', color: '#111', border: '1px solid #111', borderRadius: 8, fontWeight: 600 }}>Request changes</button>
                          </div>
                        </div>
                      )}
                      {task.role === 'Marketing Manager' && canUseMarketingTools(userRole) && transactionId && (
                        <div style={styles.detailItem}>
                          <a href={`/marketing-tools?listing=${encodeURIComponent(transactionId)}`} style={{ color: '#8C6D1F', fontWeight: 600 }}>Marketing Tools</a>
                        </div>
                      )}
                      <div style={styles.detailItem}>
                        <span style={styles.detailLabel}>Status</span>
                        <span style={styles.detailValue}>{task.status === 'completed' ? 'Done' : task.status === 'na' ? 'N/A' : 'To do'}</span>
                      </div>
                      </div>

                    <div style={{ ...styles.taskActions, marginLeft: 'auto' }}>
                      {canEditTask(task, userRole, user?.uid) ? (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" onClick={(e) => handleStatusChange(task.id, 'completed', e.currentTarget)} style={quickBtn}>Done</button>
                        <button type="button" onClick={() => handleStatusChange(task.id, 'na')} style={quickBtnNA}>N/A</button>
                      </div>
                      ) : <span style={styles.detailLabel}>View only</span>}
                    </div>

                    <div style={{ flex: '1 1 100%' }}>
                    {task.liveCheck && task.status !== 'completed' && task.status !== 'na' && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 13, color: '#5B6270', marginBottom: 6 }}>Leave this open until the listing is live on the MLS. Enter the date it went live:</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <input
                            type="date"
                            aria-label="Date listed live on MLS"
                            value={liveDates[task.id] || localToday()}
                            max={localToday()}
                            onChange={(e) => setLiveDates((p) => ({ ...p, [task.id]: e.target.value }))}
                            style={{ padding: '7px 10px', border: '1px solid #d8c9a8', borderRadius: 6, fontSize: 14 }}
                          />
                          <button
                            type="button"
                            onClick={() => handleLiveCheck(task)}
                            disabled={liveSaving === task.id}
                            style={{ padding: '8px 14px', background: '#1a1a1a', color: '#D4AF37', border: '1px solid #D4AF37', borderRadius: 6, cursor: 'pointer', fontWeight: 600, opacity: liveSaving === task.id ? 0.6 : 1 }}
                          >
                            {liveSaving === task.id ? 'Saving...' : 'Enter live date'}
                          </button>
                        </div>
                      </div>
                    )}
                    {isMarkLive(task) && (
                      <div style={{ marginBottom: 8 }}>
                        <button
                          type="button"
                          onClick={() => handleMarkLive(task)}
                          style={{ padding: '8px 14px', background: '#1a1a1a', color: '#D4AF37', border: '1px solid #D4AF37', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                        >
                          Mark Listing as Live
                        </button>
                      </div>
                    )}
                    {isClientEmailTask(task) && !done && (
                      <div style={{ marginBottom: 8, padding: '8px 10px', background: '#FFF8E6', border: '1px solid #E8D59A', borderRadius: 6, fontSize: 13, color: '#5B4A12' }}>
                        Client email: it needs your approval. On its date (8 AM) it appears under Approve email at the top of your dashboard; approving it sends it and completes this task. Only mark this Done if you sent it yourself.
                      </div>
                    )}
                    {quickOn && (<>
                      <InspectorQuickContact task={task} dealAddress={dealAddress} client={client} />
                      <StagingQuickContact task={task} deal={deal} ownerUid={ownerUid} />
                      <PhotographerQuickText task={task} deal={deal} ownerUid={ownerUid} />
                      <PhotographyClientText task={task} deal={deal} client={client} ownerUid={ownerUid} />
                      <MatterportScheduleAction task={task} transactionId={task.transactionId || transactionId} ownerUid={ownerUid} onSaved={loadTasksNow} />
                      <MatterportClientText task={task} client={client} ownerUid={ownerUid} deal={deal} />
                      <HomeSaleContactQuickContact task={task} listingAgent={listingAgent} client={client} onSent={() => handleSent(task)} />
                      <ClientQuickContact task={task} client={client} onSent={() => handleSent(task)} />
                      <TitleInitiationFlow task={task} deal={deal} ownerUid={ownerUid || user?.uid} transactionId={transactionId} onSent={() => handleSent(task)} />
                      <LenderQuickContact task={task} lender={lenderFromDeal(deal)} dealAddress={dealAddress} onSent={() => handleSent(task)} />
                      <OtherAgentQuickText task={task} otherAgent={otherAgent} dealAddress={dealAddress} senderName={user?.displayName || ''} onSent={() => handleSent(task)} />
                      <HomeSaleAgentQuickContact task={task} agent={homeSaleAgentOf(deal)} contingencyDate={deal?.underContract?.homeSaleContingencyDate} onSent={() => handleSent(task)} />
                      <SellerQuestionnaireQuickContact task={task} deal={deal} onSent={() => handleSent(task)} />
                      <ClientBirthdayQuickEntry task={task} deal={deal} ownerUid={ownerUid || user?.uid} transactionId={transactionId} onSent={() => handleSent(task)} />
                      <GoogleReviewQuickText task={task} deal={deal} ownerUid={ownerUid || user?.uid} onSent={() => handleSent(task)} />
                      <FollowUpTextQuickEntry task={task} deal={deal} onSent={() => handleSent(task)} />
                    </>)}
                    <FeatureCardsFlow task={task} txId={task.transactionId || transactionId} />
                    <MaterialApprovalFlow task={task} txId={task.transactionId || transactionId} onChanged={loadTasksNow} />
                    <FeatureUpgradeFlow task={task} txId={task.transactionId || transactionId} />
                    <SocialGraphicsFlow task={task} txId={task.transactionId || transactionId} />

                    {isCloseTask(task) && !done && canEditTask(task, userRole, user?.uid) && (
                      <div style={styles.closingRow}>
                        <label style={styles.detailLabel}>Closed (Settlement) Date:</label>
                        <input
                          type="date"
                          value={closingInputs[task.id] || defaultCloseDate()}
                          max={todayIso()}
                          onChange={(e) => setClosingInputs({ ...closingInputs, [task.id]: e.target.value })}
                          style={styles.closingInput}
                        />
                        <button type="button" onClick={() => handleMarkClosed(task)} disabled={closeBusy} style={{ ...quickBtn, opacity: closeBusy ? 0.6 : 1 }}>
                          {closeBusy ? 'Saving...' : 'Mark as Closed'}
                        </button>
                        <button type="button" onClick={() => { setNotClosingFor(notClosingFor === task.id ? null : task.id); setNewCloseDate(''); }} disabled={closeBusy} style={quickBtnNA}>
                          No, not closing today
                        </button>
                        {notClosingFor === task.id && (
                          <div style={{ flex: '1 1 100%', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
                            <label style={styles.detailLabel}>New anticipated closing date:</label>
                            <input type="date" value={newCloseDate} min={plusDays(todayIso(), 1)} onChange={(e) => setNewCloseDate(e.target.value)} style={styles.closingInput} />
                            <button type="button" onClick={() => handleNotClosing(task)} disabled={closeBusy || !newCloseDate} style={{ ...quickBtn, opacity: closeBusy || !newCloseDate ? 0.6 : 1 }}>
                              {closeBusy ? 'Saving...' : 'Save new date'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    </div>
                  </div>
                  </React.Fragment>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  hidden: { display: 'none' },
  container: {
    marginTop: '40px',
    paddingTop: '24px',
    borderTop: `1px solid ${LINE}`,
    color: INK,
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '16px',
    gap: '12px',
  },
  sectionTitle: { fontSize: '18px', fontWeight: '700', margin: '0', color: INK },
  scoreRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  scorePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '999px',
    border: `1px solid ${GOLD}`,
    background: '#FFFFFF',
    color: INK,
    fontSize: '12px',
    fontWeight: '700',
  },
  celebration: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '12px 16px',
    marginBottom: '16px',
    borderRadius: '10px',
    border: `1px solid ${GOLD}`,
    background: '#FFFFFF',
    boxShadow: '0 2px 10px rgba(184, 150, 46, 0.25)',
  },
  celebrationTitle: { fontSize: '15px', color: GOLD },
  celebrationText: { fontSize: '13px', color: MUTED },
  progressWrap: { marginBottom: '16px' },
  progressLabelRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '6px' },
  progressLabel: { fontSize: '12px', fontWeight: '600', color: MUTED },
  progressTrack: { height: '10px', borderRadius: '999px', background: '#ECEEF2', overflow: 'hidden' },
  progressFill: {
    height: '100%',
    borderRadius: '999px',
    background: `linear-gradient(90deg, #e3c35f 0%, ${GOLD} 100%)`,
    transition: 'width 0.4s ease',
  },
  toolRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '16px',
  },
  filterLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: MUTED,
    fontWeight: '500',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  checkbox: { width: '16px', height: '16px', cursor: 'pointer', accentColor: GOLD },
  tasksList: { display: 'flex', flexDirection: 'column', gap: '16px' },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(72px, 1fr))',
    gap: '8px',
    maxWidth: '100%',
  },
  statItemActive: { borderColor: GOLD, boxShadow: `inset 0 0 0 1px ${GOLD}`, background: '#FFFBEF' },
  statItem: {
    background: '#FFFFFF',
    border: `1px solid ${LINE}`,
    borderRadius: '10px',
    padding: '10px 8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
    minWidth: 0,
    textAlign: 'center',
  },
  statLabel: { fontSize: '12px', color: MUTED, fontWeight: '500' },
  statValue: { fontSize: '16px', fontWeight: '700', color: GOLD },
  tasksTable: { display: 'flex', flexDirection: 'column', gap: '12px' },
  taskRow: {
    background: '#FFFFFF',
    border: `1px solid ${LINE}`,
    borderLeft: `4px solid ${GOLD}`,
    borderRadius: '10px',
    padding: '16px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 1px 3px rgba(20, 24, 33, 0.06)',
    transition: 'all 0.2s ease',
  },
  taskMainSection: { display: 'flex', gap: '12px', flex: '1 1 260px', minWidth: 0 },
  taskStatusIcon: {
    minWidth: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  taskContent: { flex: 1, minWidth: 0 },
  taskTitle: { fontSize: '15px', fontWeight: '700', margin: '0 0 4px 0', color: INK },
  taskDescription: { fontSize: '13px', color: MUTED, margin: '0', lineHeight: 1.45 },
  taskDetailsSection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(80px, 1fr))',
    gap: '12px',
  },
  detailItem: { display: 'flex', flexDirection: 'column', gap: '2px', whiteSpace: 'nowrap' },
  detailLabel: {
    fontSize: '10px',
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '600',
  },
  detailValue: { fontSize: '13px', color: INK, fontWeight: '600' },
  taskActions: { minWidth: '130px' },
  statusSelect: {
    width: '100%',
    padding: '8px',
    backgroundColor: '#FFFFFF',
    border: `1px solid ${LINE}`,
    borderRadius: '6px',
    color: INK,
    fontSize: '13px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  viewToggle: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  closingRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '8px 0' },
  closingInput: { padding: '6px 8px', border: `1px solid ${LINE}`, borderRadius: '6px', fontSize: '13px' },
  toggleBtn: {
    padding: '8px 14px',
    backgroundColor: '#FFFFFF',
    border: `1px solid ${LINE}`,
    borderRadius: '8px',
    color: MUTED,
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  toggleBtnActive: {
    padding: '8px 14px',
    background: 'linear-gradient(135deg, #f5e09a 0%, #e3c35f 45%, #b8962e 100%)',
    border: `1px solid ${GOLD}`,
    borderRadius: '8px',
    color: '#1a1400',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  badge: {
    marginLeft: '8px',
    padding: '2px 6px',
    border: `1px solid ${GOLD}`,
    borderRadius: '4px',
    color: GOLD,
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    verticalAlign: 'middle',
  },
  box: {
    background: '#FFFFFF',
    border: `1px solid ${LINE}`,
    borderRadius: '10px',
    padding: '20px',
    textAlign: 'center',
  },
  mutedText: { color: MUTED, fontSize: '13px', margin: '0' },
  errorBox: {
    background: '#FFF0F0',
    border: '1px solid #D64545',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '16px',
  },
  errorText: { color: '#8A1F1F', fontSize: '13px', margin: '0' },
};

// Quick Done / N/A buttons.
const quickBtn = { padding: '8px 14px', borderRadius: 6, border: '1px solid #3C9D5D', background: '#3C9D5D', color: '#FFFFFF', fontWeight: 700, fontSize: 13, cursor: 'pointer' };
const quickBtnNA = { padding: '8px 14px', borderRadius: 6, border: '1px solid #9AA1AD', background: '#FFFFFF', color: '#5B6270', fontWeight: 700, fontSize: 13, cursor: 'pointer' };
